import api from './api';

export interface PagaliUser {
  user_id: string;
  nome: string;
  email?: string;
  username?: string;
  telefone?: string;
}

export interface PagaliEntidade {
  id: string;
  nome: string;
  nif?: string;
  email?: string;
  telefone?: string;
  contacto_nome?: string;
  morada?: string;
  estado: string;
}

export interface CorporateClient {
  id: number;
  nome: string;
  nif?: string;
  entidade_id?: number;
  contacto_nome?: string;
  contacto_email?: string;
  contacto_telefone?: string;
  wallet_corporativa_id?: string;
  plano: 'STARTER' | 'PRO' | 'ENTERPRISE';
  reward_day: number;
  ativo: number;
  total_funcionarios: number;
  funcionarios_ativos: number;
  bracelets_ativos: number;
  created_at: string;
}

export interface CorporateEmployee {
  id: number;
  corporate_client_id: number;
  client_id?: string;
  nome: string;
  email?: string;
  telefone?: string;
  departamento?: string;
  cargo?: string;
  reward_amount: number;
  nfc_wristband_id?: number;
  nfc_uid?: string;
  ativo: number;
  saldo?: number;
  saldo_bonus?: number;
  saldo_total?: number;
}

export interface CorporateBracelet {
  employee_id: number;
  employee_nome: string;
  departamento?: string;
  nfc_uid?: string;
  nfc_wristband_id?: number;
  company_id: number;
  company_nome: string;
  bracelet_status?: string;
  wallet_id?: string;
  saldo_bonus: number;
  stock_status?: string;
}

export interface RewardDistribution {
  id: number;
  corporate_client_id: number;
  employee_id: number;
  employee_nome: string;
  departamento?: string;
  cargo?: string;
  amount: number;
  periodo: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED';
  created_at: string;
}

export interface CorporateStats {
  empresa: string;
  funcionarios: {
    total: number;
    ativos: number;
    com_bracelet: number;
    budget_mensal: number;
  };
  distribuicoes: {
    total_distribuido: number;
    total_concluido: number;
    distribuido_mes_atual: number;
    total_operacoes: number;
  };
}

const corporateService = {
  // ── Pesquisa de Entidades Pagali ──────────────────────────────

  searchUsers: (q: string) =>
    api.get('/admin/corporate/users/search', { params: { q } })
      .then(r => r.data.data as PagaliUser[]),

  searchEntidades: (q: string) =>
    api.get('/admin/corporate/entities/search', { params: { q } })
      .then(r => r.data.data as PagaliEntidade[]),

  // ── Empresas ──────────────────────────────────────────────────

  getAll: () =>
    api.get('/admin/corporate').then(r =>
      (r.data.data as any[]).map(c => ({ ...c, id: c.id ?? c.ID })) as CorporateClient[]
    ),

  create: (data: Partial<CorporateClient>) =>
    api.post('/admin/corporate', data).then(r => r.data),

  update: (id: number, data: Partial<CorporateClient>) =>
    api.put(`/admin/corporate/${id}`, data).then(r => r.data),

  remove: (id: number) =>
    api.delete(`/admin/corporate/${id}`).then(r => r.data),

  getStats: (id: number) =>
    api.get(`/admin/corporate/${id}/stats`).then(r => r.data.data as CorporateStats),

  // ── Funcionários ──────────────────────────────────────────────

  getEmployees: (clientId: number) =>
    api.get(`/admin/corporate/${clientId}/employees`).then(r => r.data.data as CorporateEmployee[]),

  createEmployee: (clientId: number, data: Partial<CorporateEmployee>) =>
    api.post(`/admin/corporate/${clientId}/employees`, data).then(r => r.data),

  updateEmployee: (clientId: number, eid: number, data: Partial<CorporateEmployee>) =>
    api.put(`/admin/corporate/${clientId}/employees/${eid}`, data).then(r => r.data),

  removeEmployee: (clientId: number, eid: number) =>
    api.delete(`/admin/corporate/${clientId}/employees/${eid}`).then(r => r.data),

  assignBracelet: (clientId: number, eid: number, nfc_uid: string) =>
    api.post(`/admin/corporate/${clientId}/employees/${eid}/bracelet`, { nfc_uid }).then(r => r.data),

  // ── Distribuições ─────────────────────────────────────────────

  getDistributions: (clientId: number, params?: { periodo?: string; status?: string }) =>
    api.get(`/admin/corporate/${clientId}/distributions`, { params }).then(r => r.data.data as RewardDistribution[]),

  triggerDistribution: (clientId: number, periodo?: string) =>
    api.post(`/admin/corporate/${clientId}/distributions/trigger`, { periodo }).then(r => r.data),

  getBracelets: (params?: { company_id?: number; status?: string }) =>
    api.get('/admin/corporate/bracelets', { params }).then(r => r.data.data as CorporateBracelet[]),
};

export default corporateService;
